import { refreshWaiting } from './waiting'
import type { HubLang } from './i18n'
import { CONTESTS, NOTIFICATIONS, supportStarLevelWords } from './hubCopy'
import { SUPPORT_LANES, laneWords, type SupportLane } from './support'
import { supabase } from './supabase'
import { signedPhotoUrl } from './photos'
import { loadPostBylines } from './feed'

/**
 * The moderator's work list — FED-11's read side.
 *
 * ── WHY EVERY FIELD COMES FROM ONE RPC ──────────────────────────────────────
 *
 * moderation_queue() is SECURITY INVOKER, like 012's feed_page: RLS decides
 * what is visible and the function only shapes it. It joins the verdict to the
 * content, resolves the locale variant through app.resolve_variant() and
 * aggregates the attachments — none of which can be done from supabase-js,
 * because rule 6 makes a post two rows and there is no LATERAL in PostgREST.
 *
 * More to the point, `.from('moderation_verdict').select(...)` would not work
 * even for the columns it could reach. 028b scoped a moderator's sight of an
 * unpublished post to "there is an unresolved non-pass verdict on this row",
 * deliberately narrow so that content.moderate is not a standing view of
 * everybody's drafts. Reassembling the queue client-side means issuing those
 * reads separately, and the visibility rule was written for the joined query.
 *
 * ── WHAT IS DELIBERATELY ABSENT ─────────────────────────────────────────────
 *
 * There is no author history, no "this person has been flagged before", and no
 * count of anything per author. That is not an omission to fill in later — it
 * is one of the three constraints from the content guidelines' section 5 that
 * 020 chose to ENFORCE rather than trust, and there is an invariant test that
 * fails if a per-author column appears on this path. A moderator judges the
 * post in front of them.
 */

export type ModerationItem = {
  verdictId: string
  contentId: string
  verdict: 'flag' | 'block'
  rulesHit: string[]
  /** The model's short reason. May be null on a failed_open row. */
  reason: string | null
  /** The model's longer reasoning. */
  reasoning: string | null
  /** The check did not run. Published unchecked — see B09. */
  failedOpen: boolean
  checkedAt: string
  policyVersion: number
  authorName: string | null
  /** 300. The byline the post was written under ("Communications"), or null. */
  bylineName: string | null
  title: string | null
  body: string | null
  photos: QueuePhoto[]
  /** 'blocked' means it never published. Anything else means it went out. */
  moderationStatus: string
}

export type QueuePhoto = {
  id: string
  path: string
  width: number
  height: number
  alt: string | null
  /** Resolved lazily — the bucket is private, so every render needs a signed URL. */
  url?: string
}

/** Is this item sitting unpublished, waiting to be released or refused? */
export function isHeldBack(item: ModerationItem): boolean {
  return item.moderationStatus === 'blocked'
}

type QueueRow = {
  verdict_id: string
  content_id: string
  verdict: 'flag' | 'block'
  rules_hit: string[] | null
  reason: string | null
  reasoning: string | null
  failed_open: boolean
  checked_at: string
  policy_version: number
  author_name: string | null
  title: string | null
  body: string | null
  attachments: {
    id: string
    path: string
    width: number
    height: number
    alt: string | null
  }[] | null
  moderation_status: string
}

export async function loadModerationQueue(limit = 50): Promise<ModerationItem[]> {
  const { data, error } = await supabase.rpc('moderation_queue', { p_limit: limit })
  if (error) throw error

  const items: ModerationItem[] = (data ?? []).map((r: QueueRow) => ({
    verdictId: r.verdict_id,
    contentId: r.content_id,
    verdict: r.verdict,
    rulesHit: r.rules_hit ?? [],
    reason: r.reason,
    reasoning: r.reasoning,
    failedOpen: r.failed_open,
    checkedAt: r.checked_at,
    policyVersion: r.policy_version,
    authorName: r.author_name,
    bylineName: null,
    title: r.title,
    body: r.body,
    photos: (r.attachments ?? []).map((a) => ({
      id: a.id,
      path: a.path,
      width: a.width,
      height: a.height,
      alt: a.alt,
    })),
    moderationStatus: r.moderation_status,
  }))

  // Signed URLs for every photo in the queue, in parallel. A moderator judging
  // a PPE call cannot do it from an alt text, so these are resolved eagerly
  // rather than on scroll — the queue is a work list of a few items, not a
  // feed of hundreds.
  await Promise.all(
    items.flatMap((item) =>
      item.photos.map(async (photo) => {
        photo.url = (await signedPhotoUrl(photo.path)) ?? undefined
      }),
    ),
  )

  // 300. Moderation runs unchanged on a byline post; the queue says whose
  // words they are AND whose name they went out under.
  const bylines = await loadPostBylines(items.map((i) => i.contentId))
  for (const item of items) item.bylineName = bylines.get(item.contentId)?.name ?? null

  return items
}

/**
 * How many items are waiting. Drives the nav badge.
 *
 * 0 for someone who does not moderate, not an error — this runs on every page
 * load and a refusal in the console on each employee's first paint is noise
 * that hides real faults. See the function comment in migration 029.
 */
export async function moderationQueueCount(): Promise<number> {
  const { data, error } = await supabase.rpc('moderation_queue_count')
  if (error) return 0
  return typeof data === 'number' ? data : 0
}

/**
 * The three outcomes, exactly as resolve_moderation defines them.
 *
 *   dismiss — the post is out and stays out; the mark is cleared.
 *   release — the post was held back; publish it now.
 *   remove  — take it down. A SOFT DELETE, deliberately: 011's
 *             content_blocked_is_not_published refuses a published post being
 *             marked blocked, and rightly, because `blocked` means the check
 *             stopped it BEFORE publication. A post removed afterwards went
 *             out and people saw it, so deleted_at is the record and
 *             moderation_status keeps saying what the CHECK concluded.
 *
 * The database refuses the wrong pairing rather than trusting this file:
 * dismiss on an unpublished post raises, because it would clear the mark and
 * leave the post invisible and looking approved. The UI hides the option
 * anyway, which is courtesy; the refusal is the control.
 */
export type ModerationAction = 'dismiss' | 'release' | 'remove'

export async function resolveModeration(
  verdictId: string,
  action: ModerationAction,
  note?: string,
): Promise<void> {
  const { error } = await supabase.rpc('resolve_moderation', {
    p_verdict_id: verdictId,
    p_action: action,
    p_note: note?.trim() ? note.trim() : null,
  })
  if (error) throw error
  refreshWaiting() // 325: a count on the nav or the bell may have moved
}

/**
 * FED-7 — acting on a post nobody referred, including one the check passed.
 *
 * ── WHY THIS IS NOT resolveModeration WITH A DIFFERENT ARGUMENT ─────────────
 *
 * resolve_moderation answers a referral and takes a VERDICT id; this takes a
 * CONTENT id and there may never have been a verdict at all. A single function
 * whose first argument means two different things depending on the action is
 * how the wrong post gets removed.
 *
 *   hide    reversible, immediate. The 11pm-on-a-Sunday action: stop it
 *           spreading, decide nothing.
 *   unhide  the judgement that it was fine after all.
 *   remove  the irreversible soft delete, for a decision already made.
 *
 * The asymmetry is the feature. Do not make remove reversible or hide
 * permanent to "simplify" this.
 *
 * ── AFTER remove, DO NOT READ THE POST BACK ─────────────────────────────────
 *
 * Measured, not assumed (invariant 16c): once removed, the post is invisible
 * to the moderator who removed it — app.content_is_visible() has no clause for
 * a deleted row except the author's. A caller that re-reads the row to confirm
 * gets zero rows and will conclude the removal failed. Re-read the LIST.
 */
export type FeedModerationAction = 'hide' | 'unhide' | 'remove'

export async function moderateContent(
  contentId: string,
  action: FeedModerationAction,
  note?: string,
): Promise<void> {
  const { error } = await supabase.rpc('moderate_content', {
    p_content_id: contentId,
    p_action: action,
    p_note: note?.trim() ? note.trim() : null,
  })
  if (error) throw error
  refreshWaiting() // 325: a count on the nav or the bell may have moved
}

/**
 * The bell.
 *
 * in_app notifications need no adapter — 023 marks them `sent` on insert
 * because the table IS the delivery, which is why this works with FND-14's
 * Resend sender still unbuilt.
 *
 * my_notifications() is SECURITY INVOKER over a self-only read policy, so this
 * can only ever return the caller's own rows. There is no admin exception and
 * there must not be one: an administrator able to page through what everybody
 * has been told is a per-person activity record, which is what rule 8 is for.
 */
export type Notification = {
  id: string
  kind: string
  subjectType: string
  subjectId: string | null
  params: Record<string, unknown>
  createdAt: string
  readAt: string | null
}

export async function loadNotifications(unreadOnly = false, limit = 30): Promise<Notification[]> {
  const { data, error } = await supabase.rpc('my_notifications', {
    p_unread_only: unreadOnly,
    p_limit: limit,
  })
  if (error) throw error
  return (data ?? []).map((r: any) => ({
    id: r.id,
    kind: r.kind,
    subjectType: r.subject_type,
    subjectId: r.subject_id,
    params: r.params ?? {},
    createdAt: r.created_at,
    readAt: r.read_at,
  }))
}

export async function markNotificationRead(id: string): Promise<void> {
  await supabase.rpc('mark_notification_read', { p_id: id })
}

/**
 * 332. Puts one of the caller's own rows back to unread, so something opened
 * but not yet dealt with stays at the top. Same boundary as markNotificationRead:
 * the update policy is recipient = me, so another person's id changes nothing.
 */
export async function markNotificationUnread(id: string): Promise<void> {
  await supabase.rpc('mark_notification_unread', { p_id: id })
}

/**
 * 332. The bell's two views. `new` asks the database for unread rows only;
 * `all` asks for everything, and my_notifications() puts unread first. Neither
 * filters or sorts in the browser: p_limit is the database's (rule 1), so a
 * page sorted here would hide an unread row older than the last read one.
 */
export type BellView = 'new' | 'all'
export const BELL_LIMIT = 50
const BELL_VIEW_KEY = 'oneconetec.bell.view'

/** Per viewer, per browser; a convenience, so a failed read is just the default. */
export function readBellView(): BellView {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(BELL_VIEW_KEY) === 'all' ? 'all' : 'new'
  } catch {
    return 'new'
  }
}

export function saveBellView(view: BellView): void {
  try {
    localStorage.setItem(BELL_VIEW_KEY, view)
  } catch {
    /* private window or blocked storage: the choice lasts until reload */
  }
}

/** 147. A contest bell carries its title in English and Spanish. */
function contestTitleOf(n: Notification, lang: HubLang): string {
  const es = typeof n.params.contest_title_es === 'string' ? n.params.contest_title_es : null
  const en = typeof n.params.contest_title === 'string' ? n.params.contest_title : ''
  return lang === 'es' && es ? es : en
}

/**
 * Plain English for a notification kind.
 *
 * Rendered HERE and not stored on the row, which is 023's decision and worth
 * restating because it looks like duplication: the outbox holds no rendered
 * text so that a row queued in English cannot be delivered to somebody who
 * switched to Spanish while it sat there. Late rendering is the whole point.
 *
 * UI strings, not content, so they belong with the app's own copy rather than
 * in content_variant — lib/hubCopy.ts, in English and Spanish since SHL-25.
 */
export function describeNotification(n: Notification, lang: HubLang = 'en'): string {
  // The sentences live in lib/hubCopy.ts (SHL-25) — one file a Spanish reader
  // can check. The KINDS and the branching stay here.
  const c = NOTIFICATIONS[lang]
  switch (n.kind) {
    case 'moderation.flagged': {
      const failedOpen = n.params.failed_open === true
      if (failedOpen) return c.failedOpen
      return n.params.verdict === 'block'
        ? c.blocked
        : c.flagged
    }

    /* 059's three. The bell has been able to receive comment and question
       notifications since 032 and 035 and rendered every one of them as its
       raw key — "wiki.question_asked" has been on screen, verbatim, for
       sixteen rows. The default branch below is what did that, which is worth
       knowing before adding a fourth kind somewhere and assuming it reads. */
    case 'comment.mentioned':
      return c.mentioned
    case 'comment.reply_to_you':
      return c.replyToYou
    case 'comment.on_your_post':
      return c.onYourPost

    /* 143 (FED-49). Somebody put your name on a photo. The row links to the
       post with the viewer open on THAT photo (notificationRoute). */
    case 'photo.tagged':
      return c.photoTagged
    case 'photo.tag_suggested':
      return c.photoTagSuggested

    /* 146. A judge chose your photo; the stars were paid in the same act. */
    case 'photo.contest_won': {
      // 147: the title in the reader's language, and the judges' award is
      // not a place (params.award), so it gets its own sentence.
      const title = contestTitleOf(n, lang)
      return n.params.award === 'judges'
        ? c.contestJudgesAward(title, Number(n.params.stars) || 0)
        : c.contestWon(Number(n.params.place) || 1, title, Number(n.params.stars) || 0)
    }

    /* 147. A judge disqualified your photo; the reason is a stable key, worded
       here, plus the judge's note when there is one. Only the uploader gets it. */
    case 'photo.contest_disqualified': {
      const reasons = CONTESTS[lang].reasons as Record<string, string>
      const key = typeof n.params.reason === 'string' ? n.params.reason : 'other'
      const note = typeof n.params.note === 'string' && n.params.note.trim() ? ` (${n.params.note.trim()})` : ''
      return c.contestDisqualified(contestTitleOf(n, lang), (reasons[key] ?? key) + note)
    }

    /* 152. Photo review. The requester hears the outcome; the poster hears
       only a change to their photo, and the row carries nobody's name; the
       person a review was handed to hears that it was. */
    case 'photo.review_decided':
      return c.photoReviewDecided(String(n.params.outcome ?? 'keep'), n.params.identity_corrected === true)
    case 'photo.review_changed':
      return c.photoReviewChanged(String(n.params.outcome ?? 'restrict'))
    case 'photo.review_handed':
      return c.photoReviewHanded

    /* 127 (FED-25). The check on replies. Moderators get the first two; the
       author of a reply the check took down gets the third, and reads WHY on
       the thread itself (guideline 8) — the reason is the model's sentence in
       the reply's own language, and the outbox carries no text (023). */
    case 'comment.check_flagged':
      return n.params.verdict === 'block' ? c.replyBlocked : c.replyFlagged
    case 'comment.check_failed':
      return c.replyCheckFailed
    case 'comment.check_retracted':
      return c.yourReplyRetracted

    /* Pre-059 rows, still in the outbox and left there: they are what happened.
       The kind covered a comment on your post AND a reply, which is exactly the
       conflation 059 split — so the wording has to cover both and claim
       neither. */
    case 'comment.replied':
      return c.replied

    /* HLP-22's three, and the first of them was ALREADY ON SCREEN AS ITS RAW
       KEY. `create_support_question` has enqueued `support.question_asked`
       since 089 shipped, and the default branch below rendered it verbatim —
       a lane holder's bell has been saying "support.question_asked" in
       English text for four days. 059's bug, in the migration that was
       supposed to have learned from it.

       THESE ARE THE ONLY KINDS BESIDES `moderation.*` THAT GO ANYWHERE. See
       `notificationRoute` below: the bell links on the KIND, which is why a
       laned reply could not keep arriving as `comment.on_your_post`. */
    case 'support.question_asked':
      return c.supportAsked
    case 'support.answered':
      /* Deliberately not "Someone replied". The lane is NAMED to the asker
         before they type — that is what "private to a named audience, not
         anonymous" means (16 Sep) — so being coy about it here would be the
         app forgetting its own promise. The lane itself is in `params`, but
         this string is one home for the wording and the drawer's LANE_LABELS
         is the other; naming it here would be a third. */
      return c.supportAnswered
    case 'support.asker_replied':
      return c.supportAskerReplied
    /* 119. A private question handed to one named person: the person is
       told, and so is the ASKER — their question's audience just gained a
       name, and that is theirs to know. Both link to the thread (route below
       matches on the `support.` prefix). */
    case 'support.handed_to_you':
      return c.supportHandedToYou
    case 'support.handed_on':
      return c.supportHandedOn(String(n.params?.to ?? ''))
    /* 121. A private question moved to another lane: the new lane is told
       the way it is told of any question, and the ASKER is told where it
       went, in their language — the audience of a private question never
       changes silently. An unknown lane key renders as itself, ugly on
       purpose (see default). */
    case 'support.moved_to_your_lane':
      return c.supportMovedToYourLane
    case 'support.lane_changed': {
      const to = String(n.params?.to ?? '')
      const words = (SUPPORT_LANES as readonly string[]).includes(to) ? laneWords(to as SupportLane, lang).to : to
      return c.supportLaneChanged(words)
    }
    /* 120. The asker is told when a public answer written from their private
       question goes LIVE — HLP-7's promise — and a curator is told there is a
       support-drafted answer to publish. */
    case 'support.published_as_answer':
      return c.supportPublishedAsAnswer
    /* 172. Stars on a platform ticket; the level is a stable key worded in
       SUPPORT_STAR_LEVELS, the stars are what was PAID (0 past the cap).
       Links to the thread (the `support.` prefix below). */
    case 'support.stars_awarded':
      return c.supportStarsAwarded(
        supportStarLevelWords(lang, String(n.params?.level ?? '')),
        Number(n.params?.stars) || 0,
        n.params?.raised === true,
      )
    /* 174. The lane closed the asker's private question — not "answered",
       which stays the asker's word; the row asks them to confirm and opens
       the thread (the `support.` prefix below), where the two buttons are. */
    case 'support.closed':
      return c.supportClosed
    case 'wiki.answer_drafted':
      return c.wikiAnswerDrafted

    case 'wiki.question_asked':
      // 096: a board question goes live when the check passes it, so this is
      // no longer "waiting to be published" — it is news, and the curator's
      // job is an official answer, not a publish button.
      return c.wikiAsked
    case 'wiki.question_answered':
      return c.wikiAnswered
    case 'wiki.question_declined':
      return c.wikiDeclined

    default:
      /* A kind with no case reads as its key, which is ugly on purpose: a bell
         row saying "recognition.received" is a visible bug report. Making this
         branch guess at prose would hide the omission instead. */
      return n.kind
  }
}

/**
 * WHERE A BELL ROW GOES, OR NOWHERE.
 *
 * ── THIS FUNCTION EXISTS BECAUSE MOST ROWS WENT NOWHERE ─────────────────────
 *
 * Until HLP-22 the rule was inline in AppShell and was one line:
 * `if (kind.startsWith('moderation.')) navigate('/moderation')`. Everything
 * else marked itself read and dead-ended — so "Someone commented on your post.
 * Sep 13" has been sitting in the bell as a sentence with no door behind it,
 * which is the fault David quoted when he asked for this.
 *
 * ── THE COMMENT AND WIKI KINDS HAVE A DOOR NOW (125, HLP-23, 29 Sep) ───────
 *
 * They returned null until 125, deliberately: no single post could be opened
 * (feed_page is a LIMITED page, so an older post could not be reached at all),
 * and a wrong destination reads as a broken link. 68 of 79 in-app rows on dev
 * went nowhere because of it. Now:
 *
 *   comment.*          → `/item/<subject>` — the permalink resolves what the
 *                        subject IS (a post renders with its thread open; a
 *                        Wiki One item goes to the board, opened) and says
 *                        "no longer available" when the reader cannot see it.
 *                        Resolved there, not here, because a comment kind does
 *                        not say whether it was on a post or a question.
 *   wiki.question_*    → `/wiki?open=<subject>` — the kind already says it.
 *
 * A null is still an honest answer for anything else, and must stay one.
 *
 * ── THE KIND IS THE ONLY THING IT CAN USE ───────────────────────────────────
 *
 * Which is exactly why 090b could not leave a laned reply arriving as
 * `comment.on_your_post`: there is no room in that kind for "and it is
 * private", so it would have been routed with every ordinary comment, i.e.
 * nowhere. Two new kinds is what BUYS the destination.
 */
export function notificationRoute(n: Notification): string | null {
  if (n.kind.startsWith('moderation.')) return '/moderation'
  // `#/support/:id` — the path-parameter case, taken properly rather than
  // dodged with another flat route (CLAUDE.md's standing note). Without a
  // subject there is nothing to open, and the list is the honest fallback.
  if (n.kind.startsWith('support.')) {
    return n.subjectId ? `/support/${n.subjectId}` : '/support'
  }
  // 120. A support-drafted answer waits in curator mode. There is no
  // `#/wiki/<id>` (WIK-17), so the knowledge base is the honest landing.
  if (n.kind === 'wiki.answer_drafted') return '/wiki'
  // 125. Without a subject there is nothing to open, so these stay null.
  if (!n.subjectId) return null
  if (n.kind.startsWith('wiki.question_')) return `/wiki?open=${n.subjectId}`
  // 127. A moderator's notice about a reply the check flagged or took down
  // opens the conversation in review, where the check's reasoning shows.
  if (n.kind === 'comment.check_flagged' || n.kind === 'comment.check_failed') {
    return `/item/${n.subjectId}?review=1`
  }
  if (n.kind.startsWith('comment.')) return `/item/${n.subjectId}`
  // 143. Opens the post with the viewer on the photo you were tagged in.
  // 147. A disqualification opens the contest, where the photo shows why.
  if (n.kind === 'photo.contest_disqualified') return `/contests/${n.subjectId}`
  // 152. A handed review opens the queue on it; the other two open the photo.
  if (n.kind === 'photo.review_handed') return `/photos/reviews?open=${n.subjectId}`
  // 163. A suggested tag opens the photo, where the poster decides.
  if (n.kind === 'photo.tagged' || n.kind === 'photo.tag_suggested' || n.kind === 'photo.contest_won' || n.kind === 'photo.review_decided' || n.kind === 'photo.review_changed') {
    const path = typeof n.params.storage_path === 'string' ? n.params.storage_path : null
    return path ? `/item/${n.subjectId}?photo=${encodeURIComponent(path)}` : `/item/${n.subjectId}`
  }
  return null
}

/**
 * 125 (HLP-23). Clears the caller's own unread in-app rows and returns how
 * many. Before this the only way to put the bell's dot out was to press every
 * row — 61 unread `wiki.question_asked` rows on dev said what that is like.
 */
export async function markAllNotificationsRead(): Promise<number> {
  const { data, error } = await supabase.rpc('mark_all_notifications_read')
  if (error) throw error
  return (data as number) ?? 0
}

/* ── 127 (FED-25): WHAT THE CHECK SAID ───────────────────────────────────────

   The content guidelines, section 9: moderators "see the check's verdict and
   reasoning". Until 127 that was true only inside the queue, for flags and
   blocks — a PASS said nothing anywhere, so "why did this go through?" had no
   answer on screen. check_notes() returns the latest verdict on each post
   asked for and on every reply under them. content.moderate only, per row,
   no author (section 5).

   `retractedBody` is set only on a reply the CHECK took down, so a moderator
   can judge whether to put it back; comments_for() returns a retracted reply
   with no words, for everyone. */
export type CheckNote = {
  contentId: string
  /** Null for the post's own verdict. */
  commentId: string | null
  verdictId: string
  verdict: 'pass' | 'flag' | 'block'
  rulesHit: string[]
  /** The sentence the author is shown on a block. */
  reason: string | null
  /** The check's note for moderators — the thing section 9 promises. */
  reasoning: string | null
  /** No model saw it. The reasoning then says why. */
  failedOpen: boolean
  model: string | null
  policyVersion: number | null
  checkedAt: string
  /** dismiss / release / remove (posts), dismiss / restore / uphold (replies). */
  resolution: string | null
  retractedBody: string | null
}

export async function loadCheckNotes(contentIds: string[]): Promise<CheckNote[]> {
  if (contentIds.length === 0) return []
  const { data, error } = await supabase.rpc('check_notes', { p_content_ids: contentIds.slice(0, 100) })
  if (error) throw error
  return ((data ?? []) as any[]).map((r) => ({
    contentId: r.content_id,
    commentId: r.comment_id ?? null,
    verdictId: r.verdict_id,
    verdict: r.verdict,
    rulesHit: r.rules_hit ?? [],
    reason: r.reason ?? null,
    reasoning: r.reasoning ?? null,
    failedOpen: r.failed_open === true,
    model: r.model ?? null,
    policyVersion: r.policy_version ?? null,
    checkedAt: r.checked_at,
    resolution: r.resolution ?? null,
    retractedBody: r.retracted_body ?? null,
  }))
}

/** The post-level notes, by content id. */
export function postNotes(notes: CheckNote[]): Map<string, CheckNote> {
  const m = new Map<string, CheckNote>()
  for (const n of notes) if (n.commentId === null) m.set(n.contentId, n)
  return m
}

/** The reply-level notes, by comment id. */
export function replyNotes(notes: CheckNote[]): Map<string, CheckNote> {
  const m = new Map<string, CheckNote>()
  for (const n of notes) if (n.commentId !== null) m.set(n.commentId, n)
  return m
}

/**
 * A moderator settles the check on a reply. `dismiss` clears a flag;
 * `restore` puts back a reply the check took down; `uphold` leaves it down.
 * The database refuses the wrong pairing (a flag cannot be restored, a block
 * cannot be dismissed), and a reply a PERSON retracted is never restored.
 */
export type ReplyCheckAction = 'dismiss' | 'restore' | 'uphold'

export async function resolveReplyCheck(
  verdictId: string,
  action: ReplyCheckAction,
  note?: string,
): Promise<void> {
  const { error } = await supabase.rpc('resolve_comment_check', {
    p_verdict_id: verdictId,
    p_action: action,
    p_note: note?.trim() ? note.trim() : null,
  })
  if (error) throw error
  refreshWaiting() // 325: a count on the nav or the bell may have moved
}
